<!-- DateRangePicker.vue with Apply button -->
<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { Calendar } from 'lucide-vue-next'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  minDate: {
    type: Date,
    default: null
  },
  maxDate: {
    type: Date,
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

const isOpen = ref(false)
const pickerEl = ref(null)
const dropdownEl = ref(null)

// Create local copy of dateRange to work with until applied
const localDateRange = ref({
  start: new Date(props.modelValue.start),
  end: new Date(props.modelValue.end)
})

// Update local dates when props change
watch(
  () => props.modelValue,
  (newVal) => {
    localDateRange.value = {
      start: new Date(newVal.start),
      end: new Date(newVal.end)
    }
  },
  { deep: true }
)

// Format date to YYYY-MM-DD for input value
const formatInputDate = (date) => {
  if (!date) return ''
  const d = new Date(date)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Format date for display
const formatDisplayDate = (date) => {
  if (!date) return ''
  const d = new Date(date)
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

// Computed properties for start and end dates
const startDate = computed({
  get: () => formatInputDate(localDateRange.value.start),
  set: (value) => {
    if (!value) return
    const start = new Date(value)
    start.setHours(0, 0, 0, 0)

    // If end date is less than start date, update end date
    const end = new Date(localDateRange.value.end)
    if (end < start) {
      localDateRange.value = { start, end: start }
    } else {
      localDateRange.value.start = start
    }
  }
})

const endDate = computed({
  get: () => formatInputDate(localDateRange.value.end),
  set: (value) => {
    if (!value) return
    const end = new Date(value)
    end.setHours(23, 59, 59, 999)

    // If start date is greater than end date, update start date
    const start = new Date(localDateRange.value.start)
    if (start > end) {
      localDateRange.value = { start: end, end }
    } else {
      localDateRange.value.end = end
    }
  }
})

// Display formatted dates based on props (not local state)
const displayValue = computed(() => {
  return `${formatDisplayDate(props.modelValue.start)} - ${formatDisplayDate(
    props.modelValue.end
  )}`
})

// Toggle dropdown
const toggleDropdown = () => {
  isOpen.value = !isOpen.value

  // Reset local date range when opening
  if (isOpen.value) {
    localDateRange.value = {
      start: new Date(props.modelValue.start),
      end: new Date(props.modelValue.end)
    }
  }
}

// Close dropdown when clicking outside
const closeOnOutsideClick = (e) => {
  if (isOpen.value && pickerEl.value && !pickerEl.value.contains(e.target)) {
    isOpen.value = false
  }
}

// Apply dates and close dropdown
const applyDates = () => {
  // Update model value with local values
  emit('update:modelValue', {
    start: localDateRange.value.start,
    end: localDateRange.value.end
  })

  // Trigger change event to notify parent
  emit('change', {
    start: localDateRange.value.start,
    end: localDateRange.value.end
  })

  // Close dropdown
  isOpen.value = false
}

// Set predefined ranges
const setPredefinedRange = (range) => {
  const now = new Date()
  const start = new Date()

  switch (range) {
    case 'today':
      start.setHours(0, 0, 0, 0)
      localDateRange.value = { start, end: now }
      break
    case 'yesterday':
      start.setDate(start.getDate() - 1)
      start.setHours(0, 0, 0, 0)
      const yesterdayEnd = new Date(start)
      yesterdayEnd.setHours(23, 59, 59, 999)
      localDateRange.value = { start, end: yesterdayEnd }
      break
    case 'week':
      start.setDate(start.getDate() - 7)
      start.setHours(0, 0, 0, 0)
      localDateRange.value = { start, end: now }
      break
    case 'month':
      start.setMonth(start.getMonth() - 1)
      start.setHours(0, 0, 0, 0)
      localDateRange.value = { start, end: now }
      break
    case 'quarter':
      start.setMonth(start.getMonth() - 3)
      start.setHours(0, 0, 0, 0)
      localDateRange.value = { start, end: now }
      break
  }
}

// Setup event handlers
onMounted(() => {
  document.addEventListener('click', closeOnOutsideClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', closeOnOutsideClick)
})
</script>

<template>
  <div ref="pickerEl" class="date-range-picker relative inline-block">
    <div
      @click="toggleDropdown"
      class="flex items-center gap-2 bg-gray-800 px-4 py-2 rounded-lg cursor-pointer hover:bg-gray-750 transition-colors"
    >
      <Calendar class="w-5 h-5 text-gray-400" />
      <span class="text-white">{{ displayValue }}</span>
      <svg
        class="w-4 h-4 text-gray-400 ml-2"
        :class="{ 'transform rotate-180': isOpen }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </div>

    <div
      v-if="isOpen"
      ref="dropdownEl"
      class="dropdown-menu origin-top-right absolute right-0 mt-2 rounded-md shadow-lg bg-gray-900 border border-gray-700 ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
      style="min-width: 280px; max-width: 320px"
    >
      <div class="p-4">
        <!-- Start and End Date Inputs -->
        <div class="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label class="text-gray-400 text-xs block mb-1">Start Date</label>
            <div class="relative">
              <input
                type="date"
                v-model="startDate"
                :min="minDate ? formatInputDate(minDate) : undefined"
                :max="endDate"
                class="bg-gray-800 text-white border border-gray-700 rounded px-2 py-1 pl-8 focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none w-full text-sm"
              />
              <div
                class="absolute left-0 top-0 h-full flex items-center pl-2 pointer-events-none"
              >
                <Calendar class="w-4 h-4 text-gray-400" />
              </div>
            </div>
          </div>

          <div>
            <label class="text-gray-400 text-xs block mb-1">End Date</label>
            <div class="relative">
              <input
                type="date"
                v-model="endDate"
                :min="startDate"
                :max="maxDate ? formatInputDate(maxDate) : undefined"
                class="bg-gray-800 text-white border border-gray-700 rounded px-2 py-1 pl-8 focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none w-full text-sm"
              />
              <div
                class="absolute left-0 top-0 h-full flex items-center pl-2 pointer-events-none"
              >
                <Calendar class="w-4 h-4 text-gray-400" />
              </div>
            </div>
          </div>
        </div>

        <!-- Quick Ranges -->
        <div class="mb-4">
          <label class="text-gray-400 text-xs block mb-2">Quick Ranges</label>
          <div class="grid grid-cols-2 gap-2">
            <button
              @click="setPredefinedRange('today')"
              class="px-2 py-1 text-xs bg-gray-800 hover:bg-gray-700 text-white rounded transition-colors"
            >
              Today
            </button>
            <button
              @click="setPredefinedRange('yesterday')"
              class="px-2 py-1 text-xs bg-gray-800 hover:bg-gray-700 text-white rounded transition-colors"
            >
              Yesterday
            </button>
            <button
              @click="setPredefinedRange('week')"
              class="px-2 py-1 text-xs bg-gray-800 hover:bg-gray-700 text-white rounded transition-colors"
            >
              Last 7 Days
            </button>
            <button
              @click="setPredefinedRange('month')"
              class="px-2 py-1 text-xs bg-gray-800 hover:bg-gray-700 text-white rounded transition-colors"
            >
              Last 30 Days
            </button>
            <button
              @click="setPredefinedRange('quarter')"
              class="px-2 py-1 text-xs bg-gray-800 hover:bg-gray-700 text-white rounded transition-colors"
            >
              Last 90 Days
            </button>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-between mt-4">
          <button
            @click="isOpen = false"
            class="px-3 py-1 text-xs bg-gray-800 hover:bg-gray-700 text-white rounded transition-colors"
          >
            Cancel
          </button>

          <button
            @click="applyDates"
            class="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors"
          >
            Apply Dates
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
/* Hide default calendar icon in date inputs */
input[type='date']::-webkit-calendar-picker-indicator {
  opacity: 0;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  cursor: pointer;
}

/* Mobile responsive styles */
@media (max-width: 640px) {
  .dropdown-menu {
    position: fixed;
    top: auto;
    left: 50%;
    transform: translateX(-50%);
    bottom: 1rem;
    width: calc(100% - 2rem);
    max-width: 320px;
    right: auto;
  }
}
</style>